/**
 * MAQUETTE — types et listes d'options statiques pour les écrans « Trunk SIP ».
 *
 * Les données métier (numéros, consommation, annuaire, SVI, restrictions…) sont
 * servies par `Domains/Service/Api/telephonie.ts` (mock en attendant le back) via
 * les hooks de `Domains/Service/Hooks/TelephonieManager.ts`. Ce fichier ne garde que
 * les types et les listes de choix d'interface qui resteront statiques même une fois
 * l'API branchée.
 */
import {InfoRow} from './common';

export interface ConsoKpi {
  label: string;
  value: string;
  sub: string;
}

export interface ConsoCaller {
  numero: string;
  nbAppels: number;
  duree: string;
  destinations: number;
  cout: string;
}

export interface ConsoDestination {
  destination: string;
  nbAppels: number;
  duree: string;
  destinations: number;
  cout: string;
}

export interface ConsoCall {
  /** ISO 8601 (ex. "2026-07-02T09:14:00+02:00") — date et heure de l'appel. */
  date: string;
  appele: string;
  nomAppelant: string;
  numAppelant: string;
  destination: string;
  /** Durée en secondes. */
  duree: number;
  type: 'Sortant' | 'Entrant';
  /** Coût en euros. */
  cout: number;
}

export interface AcdCall {
  dateHeure: string;
  appelant: string;
  utilisateur: string;
  numAppele: string;
  /** Durée en secondes. */
  duree: number;
  /** Montant en euros. */
  montant: number;
}

export interface IdentifiantsSip {
  nomUtilisateur: string;
  motDePasse: string;
  serveurSip: string;
  proxySip: string;
  npa: string;
  port: string;
}

export interface CallRedirectInfo {
  enabled: boolean;
  target: string;
}

/** Détail enrichi d'un numéro, tel que renvoyé par `GET .../number/{numberId}` (api-telephonie). */
export interface NumeroDetail {
  tariffClass: string | null;
  location: {locationId: number | null; label: string | null} | null;
  sipAccount: {username: string | null; sipServer: string | null; sipProxy: string | null} | null;
  softphone: {username: string | null} | null;
  voicemail: {
    enabled: boolean | null;
    announcementOnly: boolean | null;
    keepOnServer: boolean | null;
    email: string | null;
    language: string | null;
    /** En clair — api-telephonie la relaie sans masquage, comme G2000 avant lui. */
    password: string | null;
  } | null;
  inboundCalls: {
    unavailableRedirect: CallRedirectInfo;
    noAnswerTimeout: number | null;
    busyRedirect: CallRedirectInfo;
    unconditionalRedirect: CallRedirectInfo;
    /** Numéro de bascule technique (panne plateforme), pas un choix utilisateur ; vide si aucun. */
    backupNumber: string | null;
    parallelRingingEnabled: boolean | null;
    parallelRingingNumber: string | null;
    parallelRingingAllowed: boolean | null;
    rejectAnonymousCalls: boolean | null;
    rejectForwardedCalls: boolean | null;
    callWaiting: boolean | null;
    musicOnHold: boolean | null;
  } | null;
  outboundCalls: {callerIdHidden: boolean | null; callerIdNumber: string | null} | null;
}

export interface TrunkNumero {
  /** Identifiant du numéro côté API (`Number.numberId`). */
  numeroId: number;
  numero: string;
  attribueA: string;
  site: string[];
  /** Identifiant de la localisation d'urgence (`Location.id`) assignée à ce numéro, quand connu. */
  locationId?: number | null;
  principal: boolean;
  actif: boolean;
  /** Numéro secondaire rattaché au numéro principal du trunk (`TrunkingLinkedNumber`). */
  lie: boolean;
  /** Numéro réservé (stock non encore attribué) — ne peut pas être rattaché à une localisation d'urgence. */
  reserve: boolean;
  identifiantsSip: IdentifiantsSip;
  boiteVocale: InfoRow[];
  appelsEntrants: InfoRow[];
  limitations: InfoRow[];
  /** Numéro affiché au destinataire lors d'un appel sortant (voir « Appels sortants »). */
  numeroAffiche?: string;
  /** Présent uniquement après un `getNumeroDetail` (loupe « Consulter »). */
  detail?: NumeroDetail;
}

export interface LocalisationSite {
  id: number;
  defaut: boolean;
  nom: string;
  client: string;
  cp: string;
  ville: string;
  rue: string;
  numero: string;
  pays: string;
  /** Renseignés via l'autocomplete d'adresse (EGLS) ; effacés si l'adresse est modifiée à la main après sélection. */
  municipalityId?: number;
  egid?: string;
  inseeCode?: string;
  latitude?: number;
  longitude?: number;
}

/** Type de groupe prenant en charge une plage horaire d'un numéro global — pilote l'icône et le masquage du bouton « Détails ». */
export type TypeGroupePlageNumero = 'distribution' | 'menu' | 'poste' | 'systeme';

/** Id de plage spécial : « Vacances » (pas de vraie plage horaire, juste un état). */
export const ID_PLAGE_VACANCES = -2;
/** Id de plage spécial : « Hors plage » — capture tout ce qui n'est couvert par aucune autre plage. */
export const ID_PLAGE_HORS_PLAGE = -3;

/** Une plage horaire associée à un numéro global, et le groupe/alias qui prend en charge les appels durant cette plage. */
export interface PlageAssocieeNumero {
  idPlage: number;
  nomPlage: string;
  typeGroupe: TypeGroupePlageNumero;
  nomGroupe: string;
  alias?: string;
}

/** Un numéro global (numéro de routage), avec ses plages horaires de prise en charge associées. */
export interface NumeroGlobal {
  id: number;
  numero: string;
  numeroFormate: string;
  nom: string;
  rejetAppelsAnonymes: boolean;
  plages: PlageAssocieeNumero[];
}

/** Nom affiché quand une ligne du moniteur d'activité n'est assignée à aucun utilisateur. */
export const UTILISATEUR_NON_ATTRIBUE = 'Non attribué';

export interface PbxActiviteLigne {
  utilisateur: string;
  telephone: string;
  groupe: string;
  /** Présence/connexion (agState LOGGED_IN) — pilote l'icône sur la colonne Utilisateur. */
  connecte: boolean;
  /** Disponibilité du téléphone — pilote l'icône sur la colonne Téléphone. */
  disponible: boolean;
}

export interface AnnuaireContact {
  id: number;
  nom: string;
  numero: string;
  raccourci: string;
}

export interface AnnuaireInterneEntry {
  nom: string;
  numero: string;
}

export type JourSemaine = 'lun' | 'mar' | 'mer' | 'jeu' | 'ven' | 'sam' | 'dim';
export type Periodicite = 'aucune' | 'semaine' | 'mois' | 'annee';

export interface PeriodeActivite {
  heureDebut: string;
  heureFin: string;
}

/**
 * Une plage horaire : structure identique quel que soit le type de groupe
 * (ouverture ou vacances) — seule la donnée saisie diffère dans l'usage.
 */
export interface PlageHoraireDetail {
  id: number;
  nom: string;
  /** Date/heure de début d'activation (ISO datetime), affichée dans le détail de la plage. */
  depuis: string;
  /** Date/heure de fin d'activation (ISO datetime), optionnelle. */
  finLe?: string;
  periodicite: Periodicite;
  /** Jours concernés, uniquement si periodicite === 'semaine'. */
  jours?: JourSemaine[];
  /** De une à six périodes d'activité par jour. */
  periodes: PeriodeActivite[];
}

export interface PlageHoraireGroupe {
  id: number;
  nom: string;
  type: 'ouverture' | 'vacances';
  plages: PlageHoraireDetail[];
}

export type TypeDistribution =
  | 'cyclique'
  | 'hierarchique'
  | 'simultane'
  | 'moins_actif'
  | 'aleatoire';

/** Message à jouer + destination de déviation, pour une condition de redirection. */
export interface ReglesRedirectionCondition {
  message: string;
  devierVers: string;
}

/** Groupe d'appel du Virtual PBX (sous-onglet « Groupes »). */
export interface PbxGroupeAppel {
  id: number;
  nom: string;
  numeroCourt: string;
  typeDistribution: TypeDistribution;
  dureeSonnerieParAgent: string;
  membres: string[];
  messageAccueil: string;
  messageAttente: string;
  messageAvantReponse: string;
  musiqueAttente: string;
  nbMaxAppelantsFile: string;
  condNbMaxAppelants: ReglesRedirectionCondition;
  dureeAttenteMax: string;
  condDureeAttenteMax: ReglesRedirectionCondition;
  dureeSonnerieMax: string;
  condDureeSonnerieMax: ReglesRedirectionCondition;
  gestionAppelsRepondus: 'inactif' | 'actif';
  desactivationMessage: string;
  desactivationDevierVers: string;
  /** Version 3 = nouveau système ACD ; false = ancien groupe « classique », éligible à la migration. */
  estAcd: boolean;
  /** Alias / numéro SIP associé au groupe. */
  alias?: string;
  /** Statut actif/inactif — n'a de sens que pour les groupes ACD. */
  actif: boolean;
}

/** Type d'action associée à une touche d'un menu IVR (HuntGroup/IVRUpdate.aspx). */
export type TypeActionIvr = 'numero' | 'groupe' | 'annonce' | 'fin_appel' | 'rejouer_menu';

/** Action touche → action → destinataire d'un menu IVR. */
export interface ActionIvr {
  touche: string;
  type: TypeActionIvr;
  /** Numéro, nom de groupe ou nom d'annonce selon le type — absent pour fin_appel/rejouer_menu. */
  cible?: string;
}

/** Un IVR (menu vocal interactif) du Virtual PBX — groupe spécial de type 7. */
export interface Ivr {
  id: number;
  nom: string;
  numeroCourt: string;
  annonceAccueil?: string;
  actions: ActionIvr[];
}

export const SVI_TOUCHES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

/** Nombre maximum d'IVR configurables (GetIVRmax côté legacy). */
export const IVR_MAX = 5;

export const CONSO_TYPES = ['Appels sortants', 'Appels entrants'];

export const CONSO_TRIS = [
  'Ordre chronologique',
  'Ordre chronologique inversé',
  'Par numéro appelant',
];

export const LDAP_OPTIONS = ['LDAP CELESTE', 'LDAP Externe', 'LDAP Neutre', 'LDAP Inactif'];

export const LDAP_DESCRIPTIONS = [
  {
    titre: 'LDAP CELESTE',
    texte:
      "Cette option active le serveur LDAP intégré au Virtual. Chaque téléphone compatible disposera alors de l'annuaire d'entreprise et d'un annuaire personnel.",
  },
  {
    titre: 'LDAP Externe',
    texte:
      "Cette option permet d'activer un serveur LDAP externe (LDAP client) et de saisir vos paramètres de configuration.",
  },
  {
    titre: 'LDAP Neutre',
    texte:
      'Cette option permet de maintenir la configuration actuelle de votre service sans agir sur les paramètres existants.',
  },
  {
    titre: 'LDAP Inactif',
    texte: 'Cette option désactive le serveur LDAP.',
    alerte: 'Attention, toutes les configurations LDAP et paramètres seront effacés.',
  },
];

/** Une salle de conférence téléphonique (sous-onglet « Conference Room »). */
export interface SalleConference {
  id: number;
  nom: string;
  numero: string;
  codeAdmin: string;
  codeUtilisateur: string;
}

/** Statut d'une boîte vocale — combine les 2 booléens legacy (VoiceMailActif + AnnonceOnly). */
export type StatutBoiteVocale = 'actif' | 'annonce_seule' | 'inactif';

/** Configuration de la boîte vocale associée à un numéro global (sous-onglet « Boîte vocale »). */
export interface ConfigurationBoiteVocale {
  statut: StatutBoiteVocale;
  nomEnregistrement?: string;
  langue: string;
  email: string;
  conserverMessages: boolean;
  codeAcces: string;
}

/** Type de son géré par le composant générique de gestion de sons (ManageSound). */
export type TypeSon = 'musique_attente' | 'tonalite' | 'annonce_fichier' | 'tts';

export interface FichierSon {
  id: number;
  titre: string;
  /** Pertinent seulement pour le type « musique_attente » : quel son est actuellement utilisé. */
  actif?: boolean;
  /** Le premier son de chaque liste musique/tonalité est fourni par défaut, non supprimable. */
  parDefaut?: boolean;
  langue?: string;
  voix?: 'homme' | 'femme';
  texte?: string;
}

/** Utilisateur dont le numéro court actuel ne respecte pas le format attendu ([2-9][0-9]{3}). */
export interface UtilisateurNumeroCourtInvalide {
  id: number;
  nom: string;
  prenom: string;
  telephone: string;
  numeroCourtActuel: string;
  numeroCourtSuggere: string;
}

/** Affectation CLIR : quel numéro global un utilisateur affiche en appel sortant. */
export interface AssignationClir {
  telephone: string;
  numeroAffiche: string | null;
}

/**
 * Statut d'un matériel IP (VoIP/ManageToIP.aspx) — reproduit la classification à 5 groupes
 * mutuellement exclusifs du legacy (ordre de priorité) plutôt que de laisser le front deviner
 * via une combinaison de booléens (CanAssociate/CanBeAutoConfigured/IAD/Free).
 */
export type StatutMaterielIp = 'autre' | 'manuel' | 'multicompte' | 'disponible' | 'associe';

/** Un compte SIP (numéro + contact assigné) porté par un matériel — 1 pour un téléphone simple, N pour un IAD/passerelle. */
export interface CompteMaterielIp {
  idWhitelist: number;
  numero: string;
  numeroFormate: string;
  /** 0 = non assigné. */
  idContact: number;
  nomContact?: string;
  /** Combiné DECT/IPIU associé à ce compte, le cas échéant. */
  combineIpiu?: string;
}

export interface MaterielIp {
  id: number;
  marque: string;
  modele: string;
  imageUrl?: string;
  mac?: string;
  /** Modèles de catégorie DECT (IdCategory=45 côté legacy) : numéro de série plutôt qu'une MAC. */
  numeroSerie?: string;
  prestationId: string;
  statut: StatutMaterielIp;
  isIad: boolean;
  isCsta: boolean;
  isSoftphone: boolean;
  isIpiu: boolean;
  canDelete: boolean;
  canEdit: boolean;
  nbCompteMax: number;
  comptes: CompteMaterielIp[];
  /** Cas CSTA : id du device physique lié à ce softphone. */
  linkedIdMateriel?: number;
  guideUrl?: string;
}

/** État du chiffrement SIPS+SRTP — réservé aux offres Corporate (CanHaveSIPSSRTP côté legacy). */
export interface SipsSrtpStatut {
  disponible: boolean;
  actif: boolean;
}

export type CategorieModeleMateriel = 'standard' | 'dect';

export interface ModeleMateriel {
  id: number;
  marque: string;
  modele: string;
  categorie: CategorieModeleMateriel;
  firmwareMinimum: string;
}

/**
 * Type de modification en cours dans la popup d'association (VoIP/ManageToIP.aspx) — les 6
 * valeurs possibles de #HidTypeModification. associer/associercsta/associeripiu partagent le
 * même point d'entrée (AfficheAssocier) mais divergent en aval (device lié pour associercsta,
 * combiné DECT + vérifications de cohérence pour associeripiu) ; updatedevice ne re-sélectionne
 * pas le numéro (déjà associé), seulement le device ; attribuer ne modifie que le contact.
 */
export type TypeModificationMateriel =
  | 'associer'
  | 'associercsta'
  | 'associeripiu'
  | 'attribuer'
  | 'dissocier'
  | 'updatedevice';

/** Un contact « libre » proposable lors d'une association (VoIP.asmx/GetFreeContacts). */
export interface ContactToIp {
  id: number;
  nom: string;
}

/** Les 4 états de diagnostic d'un téléphone (bouton « Check » — 4 bulles vert/orange/gris/rouge). */
export type StatutCheckMateriel = 'ok' | 'avertissement' | 'inconnu' | 'erreur';

export type TypeAppelHistorique = 'entrant' | 'sortant';
export type ResultatAppelHistorique = 'reussi' | 'abandonne';

/** Une entrée de l'historique des appels (« Symphonie VOIP — Historique des appels »). */
export interface AppelHistorique {
  id: number;
  /** ISO 8601 (ex. "2026-08-27T10:43:16"). */
  dateHeure: string;
  type: TypeAppelHistorique;
  appelant: string;
  /** Chaîne de routage jusqu'à la destination finale (ex. ['840', '4210']) — un seul élément pour une destination externe directe. */
  destination: string[];
  resultat: ResultatAppelHistorique;
  dureeSecondes: number;
}

/**
 * « Restriction d'appels » (Mes services > Téléphonie) — 4 onglets réels côté legacy
 * (Limitation.aspx / LimitationsOutgoing.aspx / FilteredIP.aspx / LimitationAntiSpam.aspx) :
 * appels sortants, appels entrants (blacklist), filtrage IP, filtrage anti-spam.
 */

/** Les 8 catégories de destination sortante de « Voice/Limitation.aspx ». */
export type CategorieAppelSortant =
  | 'mobile'
  | 'mobile_international'
  | 'international'
  | 'renseignements'
  | 'reveil'
  | '0900'
  | '0901'
  | '0906';

export type DroitCategorieSortant = 'autorise' | 'interdit' | 'herite';

/** Une règle de restriction sortante : celle de la prestation (numero=null) ou la surcharge d'un numéro précis (limitation avancée uniquement — jamais 'herite' pour la règle de prestation). */
export interface RegleSortante {
  numero: string | null;
  droits: Record<CategorieAppelSortant, DroitCategorieSortant>;
}

export interface ParametresAppelsSortants {
  /** Pilote l'affichage du sélecteur de numéro : sans limitation avancée, seule la règle de prestation existe. */
  limitationAvancee: boolean;
  budgetMensuel09xx: number;
  budgetConsomme09xx: number;
  regles: RegleSortante[];
}

/** Une entrée de blacklist d'appels entrants. */
export interface EntreeBlacklistEntrant {
  id: number;
  numeroAppelant: string;
  /** Numéro précis de la prestation, ou '*' si la règle s'applique à tous les numéros. */
  numeroAppele: string;
}

export interface TicketAppelEntrant {
  recno: number;
  numeroAppelant: string;
  dateHeure: string;
  pdfUrl?: string;
}

export interface ExceptionAntiSpamAppel {
  idWhitelist: number;
  numero: string;
  numeroFormate: string;
  actif: boolean;
}

export interface ConfigurationAntiSpamAppel {
  tousLesNumeros: boolean;
  numero?: string;
  actif: boolean;
  exceptions: ExceptionAntiSpamAppel[];
}

/** Zones géographiques du filtrage IP. */
export type ZoneFiltrageIp = 'nulle_part' | 'suisse' | 'france' | 'europe' | 'monde';

export interface RegleFiltrageIp {
  idWhitelist: number;
  /** Numéro précis, ou '*' pour une règle « tous les numéros ». */
  numero: string;
  zone: ZoneFiltrageIp;
  teamsActif: boolean;
  exceptionsIp: string[];
}

export interface FiltrageIpDonnees {
  reglesExistantes: RegleFiltrageIp[];
  paysEurope: string[];
}

/**
 * « Configurer mon Trunk SIP » : configuration technique du trunk (SBC/PBX partenaire),
 * persistée par `api-telephonie` (ressource `BusinessTrunkConfig`, tête de trunk
 * `TrunkingMainNumber`). Champs affichés dans le formulaire, en libellés français
 * calqués sur l'écran legacy.
 *
 * `sendRemotePartyInfo`, `usePAssertedId`, `destinationAddressIsToHeader`,
 * `fromDisplayName`, `numberFormat`, `enableMsTeams` sont requis par le `PUT` de la
 * ressource mais n'ont pas de contrôle dans ce formulaire (pas d'équivalent sur
 * l'écran legacy actuel) : chargés depuis l'API et renvoyés tels quels à
 * l'enregistrement, plutôt que d'être devinés.
 */
export interface ConfigurationTrunkSip {
  modelTypeVersion: string;
  redundancyMode: string;
  enableRedirect: string;
  enableRefer: string;
  mediaHoldFormat: string;
  authenticateBusinessTrunkUser: string;
  transportMethod: string;
  callHistory: string;
  ownDomainNameInHostparts: string;
  destinationAddressPrefix: string;
  callingAddressPrefix: string;
  stripLeadingDigits: number;
  additionalDestinationAddressPrefix: string;
  userPhone: string;
  allowOutgoingCalls: string;
  allowIncomingDirectCallsToVoipNumbers: string;
  /** Lecture seule (plafond d'appels simultanés, jamais réécrit par l'écran legacy). */
  concurrentCalls: number;
  sendRemotePartyInfo: boolean;
  usePAssertedId: boolean;
  destinationAddressIsToHeader: boolean;
  fromDisplayName: string;
  numberFormat: string;
  enableMsTeams: boolean;
}
